import { Dialog, DialogContent } from "@punchup/ui-common/shadcn/ui/dialog";
import { Drawer, DrawerContent } from "@punchup/ui-common/shadcn/ui/drawer";

import { ShowWithComedian } from "@punchup/data/legacy";
import { useMediaQuery } from "@punchup/utils/hooks";
import { useMemo } from "react";

import TourDateRow from "./TourDateRow";
import TourDateRowThemed from "./TourDateRowThemed";
import { useMultipleWaitlistStatus } from "../hooks/useWaitlistStatus";

interface Props {
  shows: ShowWithComedian[];
  title: string;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  venueSlug?: string;
  venueLogoImg: string | null;
  venueLogoBackgroundColor: string;
}

export default function ShowsModalOrDrawer({
  shows,
  title,
  isOpen,
  setIsOpen,
  venueSlug,
  venueLogoImg,
  venueLogoBackgroundColor,
}: Props) {
  const isDesktop = useMediaQuery("(min-width: 768px)");

  // Batch-fetch waitlist status so each sold-out row doesn't fire its own
  // /api/v2/user/waitlist request — see WaitlistButton.injectedSubscription.
  const soldOutShowsForWaitlist = useMemo(
    () =>
      shows
        .filter((s) => s.is_sold_out)
        .map((s) => ({ id: s.id, ownerId: s.venue_id, isVenue: true })),
    [shows]
  );
  const { getSubscriptionForShow } = useMultipleWaitlistStatus(
    soldOutShowsForWaitlist
  );

  const commonContent = (
    <div className="flex flex-col gap-4 pt-4 px-4 pb-8 md:pb-0 md:pt-0 md:px-0 h-full max-h-[80vh] overflow-y-auto">
      <h1 className="text-[26px] md:text-[32px] font-bold text-primary-blue font-sans normal-case">
        {title}
      </h1>
      {shows.length === 0 ? (
        <p className="text-center text-gray-500">No shows found</p>
      ) : (
        shows.map((show) => (
          <div key={show.id} className="w-full aspect-[4/5] md:aspect-auto">
            {venueSlug ? (
              <TourDateRowThemed
                show={show}
                comedians={show.show_comedians || []}
                internalReferrer={undefined}
                venueSlug={venueSlug}
                venueLogoImg={venueLogoImg}
                cardBackgroundColor={venueLogoBackgroundColor}
                onBuyTicketsClick={() => setIsOpen(false)}
                waitlistSubscription={getSubscriptionForShow(show.id)}
              />
            ) : (
              <TourDateRow
                show={show}
                comedians={show.show_comedians || []}
                internalReferrer={undefined}
                venueLogoImg={venueLogoImg}
                cardBackgroundColor={venueLogoBackgroundColor}
                onBuyTicketsClick={() => setIsOpen(false)}
                waitlistSubscription={getSubscriptionForShow(show.id)}
              />
            )}
          </div>
        ))
      )}
    </div>
  );

  if (!isDesktop) {
    return (
      <Drawer open={isOpen} onOpenChange={setIsOpen}>
        <DrawerContent className="z-[99] font-regular flex gap-x-4">
          {commonContent}
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="rounded-lg md:w-full gap-y-0 max-w-5xl">
        {commonContent}
      </DialogContent>
    </Dialog>
  );
}
